"use client";

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiryType: 'general',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name && formData.email && formData.message) {
      setIsSubmitted(true);
      setTimeout(() => {
        setFormData({ name: '', email: '', inquiryType: 'general', message: '' });
        setIsSubmitted(false);
      }, 5000);
    }
  };

  const inquiryTypes = [
    { id: 'type_general', value: 'general', label: 'General Inquiry', icon: 'ChatBubbleLeftRightIcon' as const },
    { id: 'type_partnership', value: 'partnership', label: 'Partnership', icon: 'HandshakeIcon' as const },
    { id: 'type_press', value: 'press', label: 'Press', icon: 'NewspaperIcon' as const },
    { id: 'type_support', value: 'support', label: 'Support', icon: 'LifebuoyIcon' as const },
  ];

  return (
    <section className="py-24 px-6 bg-background">
      <div className="max-w-4xl mx-auto">
        {!isSubmitted ? (
          <form onSubmit={handleSubmit} className="glass-card p-8 md:p-12 rounded-3xl">
            {/* Name & Email */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-3">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-input border border-white/10 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:bg-white/10 transition-all"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-3">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-input border border-white/10 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:bg-white/10 transition-all"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Inquiry Type */}
            <div className="mb-6">
              <label className="block text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-4">
                Inquiry Type
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {inquiryTypes.map((type) => (
                  <label key={type.id} className="cursor-pointer">
                    <input
                      type="radio"
                      name="inquiryType"
                      value={type.value}
                      checked={formData.inquiryType === type.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div
                      className={`flex flex-col items-center justify-center p-4 rounded-xl border transition-all ${
                        formData.inquiryType === type.value
                          ? 'border-primary bg-primary/10' :'border-white/10 bg-input hover:bg-white/10'
                      }`}
                    >
                      <Icon
                        name={type.icon}
                        size={24}
                        className={formData.inquiryType === type.value ? 'text-primary' : 'text-muted-foreground'}
                      />
                      <span className={`text-xs mt-2 font-medium ${formData.inquiryType === type.value ? 'text-primary' : 'text-muted-foreground'}`}>
                        {type.label}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Message */}
            <div className="mb-8">
              <label htmlFor="message" className="block text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-3">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-4 py-3 bg-input border border-white/10 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:bg-white/10 transition-all resize-none"
                placeholder="Tell us how we can help..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-4 bg-gradient-to-r from-primary to-secondary text-white font-semibold rounded-xl hover:shadow-2xl hover:shadow-primary/50 transition-all flex items-center justify-center gap-3 group"
            >
              Send Message
              <Icon
                name="PaperAirplaneIcon"
                size={20}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>

            <p className="text-xs text-muted-foreground text-center mt-4">
              We typically respond within 24 hours during business days.
            </p>
          </form>
        ) : (
          <div className="glass-card p-12 rounded-3xl text-center">
            <Icon
              name="CheckCircleIcon"
              variant="solid"
              size={64}
              className="text-success mx-auto mb-6"
            />
            <h3 className="text-3xl font-bold text-foreground mb-4">Message Sent!</h3>
            <p className="text-lg text-muted-foreground mb-6">
              Thanks for reaching out. We&apos;ll get back to you within 24 hours.
            </p>
            <p className="text-sm text-muted-foreground">
              In the meantime, feel free to{' '}
              <a href="/homepage#waitlist" className="text-primary hover:underline">
                join our waitlist
              </a>
              .
            </p>
          </div>
        )}
      </div>
    </section>
  );
}